import axios from 'axios';
import * as CryptoJS from 'crypto-js';
import type { AxiosInstance, AxiosResponse } from 'axios';
import {
  Designation,
  DesignationForm,
  Client,
  Project,
  Invoice,
  DashboardStats,
  RecentActivity,
  RevenueData,
  Associate,
  AssociateForm,
} from '../types/index';

const secretKey = process.env.REACT_APP_AES_SECRET || 'your-strong-secret-key';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// export interface Associate {
//   _id?: string;
//   name: string;
//   email: string;
//   phone?: string;
//   department: string;
//   position: string;
//   joinDate: string;
//   salary?: string;
//   status: 'active' | 'inactive' | 'onboarding' | 'on-leave';
//   skills?: string;
//   experience?: string;
//   education?: string;
//   languages?: string;
//   emergencyContact: {
//     name: string;
//     contactNumber: string;
//   };
//   deleted?: boolean;
//   userId?: string;
// }

export interface User {
  _id?: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'onboarding' | 'on-leave';
  joinDate: string;
  associateId?: string;
  deleted?: boolean;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Ensure headers object exists
      if (!config.headers) config.headers = {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Decrypt if response contains encrypted data (for all endpoints)
    if (response.data && typeof response.data === 'object' && response.data.data) {
      try {
        const bytes = CryptoJS.AES.decrypt(response.data.data, secretKey);
        const decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        response.data = decrypted;
      } catch (e) {
        // If decryption fails, keep original
      }
    }
    return response;
  },
  (error:any) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// // API endpoints
// export const authAPI = {
//   login: (email: string, password: string) =>
//     api.post('/auth/login', { email, password }),
//   register: (userData: any) =>
//     api.post('/auth/register', userData),
//   validateToken: () =>
//     api.get('/auth/validate'),
//   logout: () =>
//     api.post('/auth/logout'),
// };

// API endpoints
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (userData: any) =>
    api.post('/auth/register', userData),
  validateToken: () =>
    api.get('/auth/validate'),
  logout: () =>
    api.post('/auth/logout'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
};


export const dashboardAPI = {
  getStats: () => api.get<DashboardStats>('/dashboard/stats'),
  getRecentActivities: () => api.get<RecentActivity[]>('/dashboard/activities'),
  getRevenueChart: (period: string) =>
    api.get<RevenueData[]>(`/dashboard/revenue?period=${period}`),
  getDashboardData: () => api.get('/dashboard'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
};

// export const dashboardAPI = {
//   getStats: () =>
//     api.get('/dashboard/stats'),
//   getRecentActivities: () =>
//     api.get('/dashboard/activities'),
//   getRevenueChart: (period: string) =>
//     api.get(`/dashboard/revenue?period=${period}`),
//   getDashboardData: () =>
//     api.get('/dashboard'),
// };

export const payrollAPI = {
  getAll: () => api.get('/payroll'),
  create: (data: any) => api.post('/payroll', data),
  update: (id: string, data: any) => api.put(`/payroll/${id}`, data),
  delete: (id: string) => api.delete(`/payroll/${id}`),
};

export const clientsAPI = {
  getAll: () => api.get<Client[]>('/clients'),
  getById: (id: string) => api.get<Client>(`/clients/${id}`),
  create: (clientData: any) => api.post<Client>('/clients', clientData),
  update: (id: string, clientData: any) => api.put<Client>(`/clients/${id}`, clientData),
  delete: (id: string) => api.delete<{ message: string }>(`/clients/${id}`),
};

export const projectsAPI = {
  getAll: () => api.get<Project[]>('/projects'),
  getById: (id: string) => api.get<Project>(`/projects/${id}`),
  create: (projectData: any) => api.post<Project>('/projects', projectData),
  update: (id: string, projectData: any) => api.put<Project>(`/projects/${id}`, projectData),
  delete: (id: string) => api.delete<{ message: string }>(`/projects/${id}`),
  getByClient: (clientId: string) => api.get<Project[]>(`/clients/${clientId}/projects`),
};

export const invoicesAPI = {
  getAll: () => api.get<Invoice[]>('/invoices'),
  getById: (id: string) => api.get<Invoice>(`/invoices/${id}`),
  create: (invoiceData: any) => api.post<Invoice>('/invoices', invoiceData),
  update: (id: string, invoiceData: any) => api.put<Invoice>(`/invoices/${id}`, invoiceData),
  delete: (id: string) => api.delete<{ message: string }>(`/invoices/${id}`),
  getByClient: (clientId: string) => api.get<Invoice[]>(`/clients/${clientId}/invoices`),
  markAsPaid: (id: string) => api.patch(`/invoices/${id}/paid`),
};

// export const userAPI = {
//   getAllUsers: () => api.get<User[]>('/users'),
//   getUserById: (id: string) => api.get<User>(`/users/${id}`),
//   createUser: (data: Omit<User, 'id'>) => api.post<User>('/users', data),
//   updateUser: (id: string, data: Partial<User>) => api.put<User>(`/users/${id}`, data),
//   softDeleteUser: (id: string) => api.delete<{ message: string }>(`/users/${id}`),

// export const clientsAPI = {
//   getAll: () =>
//     api.get('/clients'),
//   getById: (id: string) =>
//     api.get(`/clients/${id}`),
//   create: (clientData: any) =>
//     api.post('/clients', clientData),
//   update: (id: string, clientData: any) =>
//     api.put(`/clients/${id}`, clientData),
//   delete: (id: string) =>
//     api.delete(`/clients/${id}`),
// };

// export const projectsAPI = {
//   getAll: () =>
//     api.get('/projects'),
//   getById: (id: string) =>
//     api.get(`/projects/${id}`),
//   create: (projectData: any) =>
//     api.post('/projects', projectData),
//   update: (id: string, projectData: any) =>
//     api.put(`/projects/${id}`, projectData),
//   delete: (id: string) =>
//     api.delete(`/projects/${id}`),
//   getByClient: (clientId: string) =>
//     api.get(`/clients/${clientId}/projects`),
// };

// export const invoicesAPI = {
//   getAll: () =>
//     api.get('/invoices'),
//   getById: (id: string) =>
//     api.get(`/invoices/${id}`),
//   create: (invoiceData: any) =>
//     api.post('/invoices', invoiceData),
//   update: (id: string, invoiceData: any) =>
//     api.put(`/invoices/${id}`, invoiceData),
//   delete: (id: string) =>
//     api.delete(`/invoices/${id}`),
//   getByClient: (clientId: string) =>
//     api.get(`/clients/${clientId}/invoices`),
//   markAsPaid: (id: string) =>
//     api.patch(`/invoices/${id}/paid`),
// };

export const userAPI = {
  getAllUsers: (searchTerm?: string) => {
    const params = searchTerm && searchTerm.trim() !== '' ? { params: { search: searchTerm } } : {};
    return api.get<User[]>('/users', params);
  },
  getUserById: (id: string) => api.get<User>(`/users/${id}`),
  createUser: (data: Omit<User, '_id'>) => api.post<User>('/users', data),
  updateUser: (id: string, data: Partial<User>) => api.put<User>(`/users/${id}`, data),
  softDeleteUser: (id: string) => api.delete<{ message: string }>(`/users/${id}`),
  setPassword: (data: { token: string | null; password: string }) =>
    api.post('/users/set-password', data),
  // Avatar upload/remove
  uploadPhoto: (id: string, photo: string) => api.post(`/users/${id}/upload-photo`, { photo }),
  removePhoto: (id: string) => api.post(`/users/${id}/remove-photo`),
};


export const associateAPI = {
  getAllAssociates: (searchTerm?: string) => {
    const params = searchTerm && searchTerm.trim() !== '' ? { params: { search: searchTerm } } : {};
    return api.get<Associate[]>('/associates', params);
  },
  // allow sorting via query params
  getAllAssociatesSorted: (options?: { searchTerm?: string; sortField?: string; sortOrder?: 'asc' | 'desc' }) => {
    const params: any = {};
    if (options?.searchTerm && options.searchTerm.trim() !== '') params.search = options.searchTerm;
    if (options?.sortField) params.sortField = options.sortField;
    if (options?.sortOrder) params.sortOrder = options.sortOrder;
    return api.get<Associate[]>('/associates', { params });
  },
  getUnassignedAssociates: () => api.get<Associate[]>('/associates/unassigned'),
  getAssociateById: (id: string) => api.get<Associate>(`/associates/${id}`),
  createAssociate: (data: AssociateForm) => api.post<Associate>('/associates', data),
  updateAssociate: (id: string, data: Partial<AssociateForm>) => api.put<Associate>(`/associates/${id}`, data),
  softDeleteAssociate: (id: string) => api.delete<{ message: string }>(`/associates/${id}`),

  // Profile photo endpoints
  uploadPhoto: (id: string, photo: string) => api.put(`/associates/${id}/photo`, { photo }),
  removePhoto: (id: string) => api.delete(`/associates/${id}/photo`),
  // Template download and bulk upload
  downloadTemplate: () => api.get('/associates/template/download', { responseType: 'arraybuffer' }),
  uploadAssociates: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/associates/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const timesheetAPI = {
  saveTimesheet: (data: any) => api.post('/timesheet/save', data),
  getTimesheets: (associateId: string) => api.get(`/timesheet/list?associateId=${associateId}`),
  getManagerTimesheets: (managerId: string) => api.get(`/timesheet/manager-list?managerId=${managerId}`),
  updateTimesheetStatus: (id: number, status: string) => api.patch(`/timesheet/${id}/status`, { status }),
};

export const designationsAPI = {
  getAll: () => api.get<Designation[]>('/designations'),
  getById: (id: string) => api.get<Designation>(`/designations/${id}`),
  create: (data: DesignationForm) => api.post<Designation>('/designations', data),
  update: (id: string, data: Partial<DesignationForm>) => api.put<Designation>(`/designations/${id}`, data),
  softDelete: (id: string) => api.delete<{ message: string }>(`/designations/${id}`),
};

export const requestsAPI = {
  create: (data: any) => api.post('/requests', data),
  getAll: () => api.get('/requests'),
  updateStatus: (id: string, status: 'approved' | 'rejected', rejectReason?: string) =>
    api.patch(`/requests/${id}`, rejectReason ? { status, rejectReason } : { status }),
};

export default api;
